import {Box, createStyles, Switch, Text, Tooltip} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {copyLLMTableToClipboard} from 'llm/markdown/copyLLMTable';
import {parseTableData} from 'llm/markdown/LLMMarkdownUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import useHoverState from 'ui-library/hooks/useHoverState';
import {Table, Column, Cell} from 'ui-library/simple-table/SimpleTable';

interface SplitContentByCitationsResult {
  citationElements: Array<React.ReactNode>;
  nonCitationElements: Array<React.ReactNode>;
}

const splitContentByCitations = (content: Array<React.ReactNode>): SplitContentByCitationsResult => {
  const citationElements: Array<React.ReactNode> = [];
  const nonCitationElements: Array<React.ReactNode> = [];

  content.forEach((item) => {
    const isCitation =
      React.isValidElement(item) &&
      typeof item.type === 'function' &&
      item.props &&
      item.props.node &&
      item.props.node.tagName === 'citationchip';

    if (isCitation) {
      citationElements.push(item);
    } else {
      nonCitationElements.push(item);
    }
  });

  return {citationElements, nonCitationElements};
};

export interface TableWithCitationsProps {
  children: unknown;
}

export function CellWithCitations({
  citationElements,
  nonCitationElements,
  showCitations,
}: {
  citationElements: Array<React.ReactNode>;
  nonCitationElements: Array<React.ReactNode>;
  showCitations: boolean;
}): JSX.Element {
  const [isCellHovered, cellHoverProps] = useHoverState();

  return (
    <Cell hoverProps={cellHoverProps}>
      {nonCitationElements}
      <span
        className={css(!showCitations && !isCellHovered && styles.hiddenCitation)}
        {...getTestProps({component: 'CellWithCitations', element: 'citationElements'})}
      >
        {citationElements}
      </span>
    </Cell>
  );
}

const COPIED_RESET_MS = 2000;

const useCopyButtonStyles = createStyles(() => ({
  tableContainer: {
    position: 'relative',
  },
  copyButtonContainer: {
    position: 'absolute',
    top: 4,
    right: 4,
    zIndex: 1,
  },
}));

/**
 * Copy button rendered at the top-right of an LLM table. When clicked, writes an
 * HTML representation of the table (without citations) to the clipboard in a format
 * that Benchling's notebook paste handling can convert into a native notebook table.
 */
function CopyTableButton({
  headers,
  rows,
}: {
  headers: Array<string>;
  rows: Array<Array<Array<React.ReactNode>>>;
}): JSX.Element {
  const {classes} = useCopyButtonStyles();
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  const handleClick = useCallback(async () => {
    const success = await copyLLMTableToClipboard({headers, rows});
    if (!success) {
      return;
    }
    setCopied(true);
    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setCopied(false);
      timeoutRef.current = null;
    }, COPIED_RESET_MS);
  }, [headers, rows]);

  const tooltipLabel = copied ? 'Copied' : 'Copy to clipboard';
  return (
    <Box className={classes.copyButtonContainer}>
      <Tooltip label={tooltipLabel} withArrow={true}>
        <span>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            size={ButtonSize.MINI}
            icon={copied ? 'check' : 'copy'}
            aria-label="Copy table"
            iconAltText="Copy table"
            onClick={handleClick}
            {...getTestProps({component: 'TableWithCitations', element: 'copyButton'})}
          />
        </span>
      </Tooltip>
    </Box>
  );
}

export default function TableWithCitations({children}: TableWithCitationsProps): JSX.Element {
  const {classes} = useCopyButtonStyles();
  const [showCitations, setShowCitations] = useState(false);
  const tableData = parseTableData(children);
  if (!tableData) {
    return <table>{children}</table>;
  }

  const {headers, rows} = tableData;

  const processedRows = rows.map((row) => row.map((cellContent) => splitContentByCitations(cellContent)));
  const hasCitations = processedRows.some((row) => row.some((cell) => cell.citationElements.length > 0));

  const footer = hasCitations ? (
    <Switch
      classNames={{
        body: css(styles.footerSwitchBody),
      }}
      checked={showCitations}
      onChange={(event) => {
        setShowCitations(event.currentTarget.checked);
      }}
      labelPosition="right"
      label={<Text fw="normal">{showCitations ? 'Hide citations' : 'Show all citations'}</Text>}
    />
  ) : undefined;

  // Rows passed to copy: use the non-citation content only so that pasted tables do not
  // include citation chips / footnote markers that make no sense outside of the LLM context.
  const rowsForCopy: Array<Array<Array<React.ReactNode>>> = processedRows.map((row) =>
    row.map((cell) => cell.nonCitationElements)
  );

  return (
    <Box className={classes.tableContainer}>
      <CopyTableButton headers={headers} rows={rowsForCopy} />
      <Table numRows={rows.length} footer={footer}>
        {headers.map((header, index) => (
          <Column
            key={index}
            header={<Cell>{header}</Cell>}
            renderCell={(rowIndex: number) => {
              const processedCell = processedRows[rowIndex]?.[index];
              if (!processedCell) {
                return <Cell />;
              }
              return (
                <CellWithCitations
                  showCitations={showCitations}
                  citationElements={processedCell.citationElements}
                  nonCitationElements={processedCell.nonCitationElements}
                />
              );
            }}
          />
        ))}
      </Table>
    </Box>
  );
}

export const styles = StyleSheet.create({
  footerSwitchBody: {
    alignItems: 'center',
  },
  hiddenCitation: {
    visibility: 'hidden',
  },
});
